import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { CognitoUser, AuthenticationDetails, CognitoUserAttribute } from 'amazon-cognito-identity-js';
import userPool from '../auth/UserPool';
import { getClient, Client, ClientDetails, Company } from '../api/clients';
import { API_ENDPOINTS } from '../services/api/endpoints';
import { API_GATEWAYS } from '../api/apiClient';

interface User {
  email: string;
  client_id: string;
  first_name: string;
  last_name: string;
  super_admin: boolean;
  entitlements: Entitlement[];
}

interface Entitlement {
  Client_ID: string;
  Company_ID: string;
  Admin: boolean;
  Approver: boolean;
  Executive: boolean;
  Developer?: boolean;
  Viewer?: boolean;
  Super_Admin?: boolean;
}

// Impersonation session state
interface ImpersonationSession {
  active: boolean;
  session_id?: string;
  client?: {
    Client_ID: string;
    Client_Name: string;
    Client_Type: string;
    Client_Status: string;
    user_count?: number;
    company_count?: number;
  };
  company?: {
    Company_ID: string;
    Company_Name: string;
  };
  role?: {
    effective_role: string;
    is_super_admin: boolean;
    simulated_entitlements: Entitlement[];
  };
  brand?: {
    id: number;
    brand_code: string;
    brand_name: string;
    primary_color: string;
    secondary_color?: string;
    accent_color?: string;
    logo_url?: string;
    logo_dark_url?: string;
    favicon_url?: string;
    enabled_features?: string[];
    enabled_agents?: string[];
  };
  started_at?: string;
  reason?: string;
}

// Valid roles for impersonation
type SimulatedRole = 'Super_Admin' | 'Admin' | 'Developer' | 'Viewer' | 'Approver';

interface ImpersonationOptions {
  clientId: string;
  companyId?: string;
  simulatedRole?: SimulatedRole;
  brandId?: number;
  reason?: string;
}

// Registration result type
interface RegisterResult {
  needsVerification: boolean;
  email: string;
  username: string;
}

interface AuthContextType {
  user: User | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  errorMessage: string | null;
  selectedClientId: string | null;
  selectedCompanyId: string | null;
  hasClientContext: boolean;
  // Impersonation state
  impersonation: ImpersonationSession;
  simulatedRole: SimulatedRole | null;
  activeBrand: ImpersonationSession['brand'] | null;
  // Auth methods
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  confirmForgotPassword: (email: string, code: string, newPassword: string) => Promise<void>;
  // Registration methods
  register: (email: string, password: string, name: string) => Promise<RegisterResult>;
  confirmRegistration: (username: string, code: string) => Promise<void>;
  resendVerificationCode: (username: string) => Promise<void>;
  clearError: () => void;
  // Impersonation methods
  setClientContext: (clientId: string, companyId: string, options?: { navigate?: boolean }) => Promise<void>;
  clearClientContext: () => void;
  impersonate: (options: ImpersonationOptions) => Promise<ImpersonationSession>;
  endImpersonation: () => Promise<void>;
  setSimulatedRole: (role: SimulatedRole) => Promise<void>;
  setBrand: (brandId: number | null) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Default impersonation state
const defaultImpersonation: ImpersonationSession = { active: false };

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);
  const [hasClientContext, setHasClientContext] = useState(false);

  // Impersonation state
  const [impersonation, setImpersonation] = useState<ImpersonationSession>(defaultImpersonation);
  const [simulatedRole, setSimulatedRoleState] = useState<SimulatedRole | null>(null);
  const [activeBrand, setActiveBrand] = useState<ImpersonationSession['brand'] | null>(null);

  // Clear error helper
  const clearError = useCallback(() => {
    setErrorMessage(null);
  }, []);

  /**
   * Decode JWT token and check if it's expired
   * Returns true if token is valid and not expired
   */
  const isTokenValid = (token: string): boolean => {
    try {
      // JWT structure: header.payload.signature
      const parts = token.split('.');
      if (parts.length !== 3) {
        return false;
      }

      // Decode payload (base64url)
      const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));

      // Check expiration (exp is in seconds, Date.now() is in milliseconds)
      if (payload.exp && payload.exp * 1000 < Date.now()) {
        console.log('[AuthContext] Token is expired');
        return false;
      }

      return true;
    } catch (error) {
      console.error('[AuthContext] Error decoding token:', error);
      return false;
    }
  };

  // Load auth state from localStorage on mount and validate token
  useEffect(() => {
    const validateAndLoadAuth = async () => {
      const storedToken = localStorage.getItem('accessToken');
      const storedUser = localStorage.getItem('user');
      const storedClientId = localStorage.getItem('selectedClientId');
      const storedCompanyId = localStorage.getItem('selectedCompanyId');

      if (storedToken && storedUser) {
        // First check if token is expired (client-side validation)
        if (!isTokenValid(storedToken)) {
          console.log('[AuthContext] Token is invalid or expired (client-side check), clearing auth state');
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
          localStorage.removeItem('selectedClientId');
          localStorage.removeItem('selectedCompanyId');
          setIsLoading(false);
          return;
        }

        // Token is valid client-side, restore auth state
        // Server validation happens on first API call
        console.log('[AuthContext] Restoring auth state from localStorage');
        setAccessToken(storedToken);
        setUser(JSON.parse(storedUser));

        if (storedClientId && storedCompanyId) {
          setSelectedClientId(storedClientId);
          setSelectedCompanyId(storedCompanyId);
          setHasClientContext(true);
        }

        // Restore impersonation state if exists
        const storedImpersonation = localStorage.getItem('impersonation');
        if (storedImpersonation) {
          try {
            const parsedImpersonation = JSON.parse(storedImpersonation) as ImpersonationSession;
            if (parsedImpersonation.active) {
              setImpersonation(parsedImpersonation);
              if (parsedImpersonation.role?.effective_role) {
                setSimulatedRoleState(parsedImpersonation.role.effective_role as SimulatedRole);
              }
              if (parsedImpersonation.brand) {
                setActiveBrand(parsedImpersonation.brand);
              }
            }
          } catch (e) {
            console.error('[AuthContext] Failed to parse stored impersonation:', e);
            localStorage.removeItem('impersonation');
          }
        }
      }

      setIsLoading(false);
    };

    validateAndLoadAuth();
  }, []);

  // Following Tim-Combo's pattern: First check user status after authentication
  const checkUserStatus = async (email: string, token: string) => {
    console.log('[AuthContext] Starting checkUserStatus for:', email);
    try {
      // Wait a moment to ensure auth token is ready (Tim-Combo pattern)
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Get user data first to check super admin status (Tim-Combo's Get_User call)
      console.log('[AuthContext] Fetching user data with /api/auth/authMe...');
      const response = await fetch(API_ENDPOINTS.AUTH.ME, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({})  // Empty body for POST request
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || 'Failed to get user details');
      }

      // Extract user from Records array (Tim-Combo pattern)
      const userData = result.data.Records?.[0];
      const isSuperAdmin = Boolean(userData?.Super_Admin);

      console.log('[AuthContext] User data fetched, Is_Super_Admin:', isSuperAdmin);

      // Update user state with full user data
      const user = {
        email: userData.Email || email,
        client_id: userData.Client_ID,
        first_name: userData.First_Name,
        last_name: userData.Last_Name,
        super_admin: isSuperAdmin,
        entitlements: []
      };

      localStorage.setItem('user', JSON.stringify(user));
      setUser(user);

      // Route based on user status (Tim-Combo pattern)
      if (isSuperAdmin) {
        console.log('[AuthContext] Super admin detected, navigating to /super-admin');
        // Navigate to super admin dashboard - will be handled by App.tsx RootRedirect
        window.location.href = '/super-admin';
        return; // Exit early for super admins
      }

      console.log('[AuthContext] Non-super user, would fetch entitlements here...');
      // For non-super users, would fetch entitlements here
      // But for now we're focused on super admin flow

    } catch (error) {
      console.error('[AuthContext] Failed to check user status:', error);
      throw error;
    }
  };

  const login = async (email: string, password: string) => {
    setIsLoading(true);
    try {
      if (!userPool) {
        throw new Error('Cognito User Pool not initialized');
      }

      console.log('[AuthContext] Authenticating with Cognito:', email);

      // Create Cognito user and auth details
      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: userPool
      });

      const authDetails = new AuthenticationDetails({
        Username: email,
        Password: password
      });

      // Authenticate with Cognito
      const result = await new Promise<any>((resolve, reject) => {
        cognitoUser.authenticateUser(authDetails, {
          onSuccess: (session) => {
            resolve(session);
          },
          onFailure: (err) => {
            reject(err);
          },
          newPasswordRequired: (userAttributes) => {
            reject(new Error('New password required - please contact admin'));
          }
        });
      });

      // Get tokens from Cognito session
      const idToken = result.getIdToken().getJwtToken();
      const accessToken = result.getAccessToken().getJwtToken();
      const refreshToken = result.getRefreshToken().getToken();

      console.log('[AuthContext] Cognito authentication successful');

      // Store tokens
      localStorage.setItem('accessToken', idToken);
      localStorage.setItem('refreshToken', refreshToken);
      setAccessToken(idToken);

      // Now fetch user details from our API using the real Cognito token
      await checkUserStatus(email, idToken);

    } catch (error: any) {
      console.error('[AuthContext] Login error:', error);
      // Provide user-friendly error messages
      if (error.code === 'NotAuthorizedException') {
        throw new Error('Incorrect email or password');
      } else if (error.code === 'UserNotFoundException') {
        throw new Error('User not found');
      } else if (error.code === 'UserNotConfirmedException') {
        throw new Error('Please verify your email first');
      }
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Register a new user with Cognito
   * Returns registration result with needsVerification flag
   */
  const register = useCallback(async (email: string, password: string, name: string): Promise<RegisterResult> => {
    if (!userPool) {
      console.error('[Register] Cognito not configured');
      setErrorMessage('Cognito not configured');
      throw new Error('Cognito not configured');
    }

    console.log('[Register] Starting registration for:', email);
    setIsLoading(true);
    setErrorMessage(null);

    try {
      // Build Cognito attributes
      const attributeList: CognitoUserAttribute[] = [
        new CognitoUserAttribute({
          Name: 'email',
          Value: email
        }),
        new CognitoUserAttribute({
          Name: 'name',
          Value: name
        })
      ];

      // Use email as username (Cognito user pool configured with email as username)
      const username = email;

      await new Promise<void>((resolve, reject) => {
        console.log('[Register] Calling Cognito signUp with username:', username);
        userPool!.signUp(username, password, attributeList, [], (err, result) => {
          if (err) {
            console.error('[Register] Cognito signUp FAILURE:', err);
            reject(err);
            return;
          }
          console.log('[Register] Cognito signUp SUCCESS:', result);
          resolve();
        });
      });

      setIsLoading(false);
      console.log('[Register] Registration completed, verification email sent');

      return { needsVerification: true, email, username };

    } catch (error: any) {
      console.error('Registration failed:', error);

      // Parse Cognito error messages for user-friendly display
      let errMsg = 'Registration failed';
      if (error.code === 'UsernameExistsException') {
        errMsg = 'This email is already registered. Please sign in or use a different email.';
      } else if (error.code === 'InvalidPasswordException') {
        errMsg = 'Password must be at least 8 characters with uppercase, lowercase, number, and special character';
      } else if (error.code === 'InvalidParameterException') {
        errMsg = 'Invalid email or password format';
      } else if (error.message) {
        errMsg = error.message;
      }

      setIsLoading(false);
      setErrorMessage(errMsg);
      throw error;
    }
  }, []);

  /**
   * Confirm user registration with verification code
   */
  const confirmRegistration = useCallback(async (username: string, code: string): Promise<void> => {
    if (!userPool) {
      console.error('[Confirm] Cognito not configured');
      setErrorMessage('Cognito not configured');
      throw new Error('Cognito not configured');
    }

    console.log('[Confirm] Confirming registration for username:', username);
    setIsLoading(true);
    setErrorMessage(null);

    try {
      const userData = { Username: username, Pool: userPool };
      const cognitoUser = new CognitoUser(userData);

      await new Promise<void>((resolve, reject) => {
        console.log('[Confirm] Calling Cognito confirmRegistration...');
        cognitoUser.confirmRegistration(code, true, (err, result) => {
          if (err) {
            console.error('[Confirm] Cognito confirmRegistration FAILURE:', err);
            reject(err);
            return;
          }
          console.log('[Confirm] Cognito confirmRegistration SUCCESS:', result);
          resolve();
        });
      });

      console.log('[Confirm] Email verified successfully');
      setIsLoading(false);

    } catch (error: any) {
      console.error('Confirmation failed:', error);

      let errMsg = 'Verification failed';
      if (error.code === 'CodeMismatchException') {
        errMsg = 'Invalid verification code';
      } else if (error.code === 'ExpiredCodeException') {
        errMsg = 'Verification code has expired. Please request a new one.';
      } else if (error.message) {
        errMsg = error.message;
      }

      setIsLoading(false);
      setErrorMessage(errMsg);
      throw error;
    }
  }, []);

  /**
   * Resend verification code to user's email
   */
  const resendVerificationCode = useCallback(async (username: string): Promise<void> => {
    if (!userPool) {
      console.error('[Resend] Cognito not configured');
      throw new Error('Cognito not configured');
    }

    console.log('[Resend] Resending verification code for username:', username);
    setIsLoading(true);
    setErrorMessage(null);

    try {
      const userData = { Username: username, Pool: userPool };
      const cognitoUser = new CognitoUser(userData);

      await new Promise<void>((resolve, reject) => {
        cognitoUser.resendConfirmationCode((err, result) => {
          if (err) {
            console.error('[Resend] Failed:', err);
            reject(err);
            return;
          }
          console.log('[Resend] Success:', result);
          resolve();
        });
      });

      setIsLoading(false);
      console.log('[Resend] Verification code sent successfully');
    } catch (error: any) {
      console.error('Resend failed:', error);
      setIsLoading(false);
      setErrorMessage(error.message || 'Failed to resend verification code');
      throw error;
    }
  }, []);

  const logout = async () => {
    try {
      if (accessToken) {
        await fetch(`${API_GATEWAYS.CORE}/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear local state regardless of API success
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      localStorage.removeItem('selectedClientId');
      localStorage.removeItem('selectedCompanyId');
      setAccessToken(null);
      setUser(null);
      setSelectedClientId(null);
      setSelectedCompanyId(null);
      setHasClientContext(false);
    }
  };

  const refreshToken = async () => {
    try {
      const storedRefreshToken = localStorage.getItem('refreshToken');

      if (!storedRefreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await fetch(`${API_GATEWAYS.CORE}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken: storedRefreshToken }),
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error('Token refresh failed');
      }

      // Extract data from Records array (standards-compliant format)
      const authData = result.data.Records?.[0] || result.data;

      // Update tokens and user data
      localStorage.setItem('accessToken', authData.accessToken);
      localStorage.setItem('refreshToken', authData.refreshToken);
      localStorage.setItem('user', JSON.stringify(authData.user));

      setAccessToken(authData.accessToken);
      setUser(authData.user);

    } catch (error) {
      console.error('Token refresh error:', error);
      // If refresh fails, logout user
      await logout();
      throw error;
    }
  };

  const setClientContext = async (
    clientId: string,
    companyId: string,
    options?: { navigate?: boolean }
  ) => {
    console.log('[AuthContext] setClientContext called:', {
      clientId,
      companyId,
      isSuperAdmin: user?.super_admin,
      navigate: options?.navigate
    });

    // Only super admins can set client context
    if (!user?.super_admin) {
      console.log('[AuthContext] setClientContext ignored - user is not super admin');
      return;
    }

    try {
      setIsLoading(true);

      // Fetch client details
      console.log('[AuthContext] Fetching client details...');
      const clientResponse = await getClient(clientId);

      if (!clientResponse.success) {
        throw new Error('Failed to get client details');
      }

      const clientData = clientResponse.data.Records[0] as ClientDetails;

      if (!clientData) {
        throw new Error('Client not found');
      }

      // Verify company exists in client's companies
      const company = clientData.companies?.find((c: Company) => c.Company_ID === companyId);

      if (!company) {
        console.warn('[AuthContext] Company not found in client companies, proceeding anyway');
      }

      // Update state
      setSelectedClientId(clientId);
      setSelectedCompanyId(companyId);
      setHasClientContext(true);

      // Store in localStorage
      localStorage.setItem('selectedClientId', clientId);
      localStorage.setItem('selectedCompanyId', companyId);

      console.log('[AuthContext] Client context set successfully:', {
        clientId,
        companyId,
        clientName: clientData.Client_Name,
        companyName: company?.Company_Name
      });

      // Navigation would be handled by the calling component if needed
      // This keeps the context provider focused on state management

    } catch (error) {
      console.error('[AuthContext] Failed to set client context:', error);

      // On error, still set the IDs but log the failure
      setSelectedClientId(clientId);
      setSelectedCompanyId(companyId);
      setHasClientContext(true);

      localStorage.setItem('selectedClientId', clientId);
      localStorage.setItem('selectedCompanyId', companyId);

      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const clearClientContext = () => {
    console.log('[AuthContext] clearClientContext called');

    if (!user?.super_admin) {
      console.log('[AuthContext] clearClientContext ignored - user is not super admin');
      return;
    }

    setSelectedClientId(null);
    setSelectedCompanyId(null);
    setHasClientContext(false);
    setImpersonation(defaultImpersonation);
    setSimulatedRoleState(null);
    setActiveBrand(null);

    localStorage.removeItem('selectedClientId');
    localStorage.removeItem('selectedCompanyId');
    localStorage.removeItem('impersonation');

    console.log('[AuthContext] Client context cleared');
  };

  /**
   * Full impersonation with role simulation and brand switching
   * Calls the backend API for audit trail and validation
   */
  const impersonate = async (options: ImpersonationOptions): Promise<ImpersonationSession> => {
    console.log('[AuthContext] impersonate called:', options);

    if (!user?.super_admin) {
      throw new Error('Only Super Admins can impersonate');
    }

    if (!accessToken) {
      throw new Error('Not authenticated');
    }

    try {
      setIsLoading(true);

      const response = await fetch(`${API_GATEWAYS.CORE}${API_ENDPOINTS.SUPERADMIN.IMPERSONATE}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          Client_ID: options.clientId,
          Company_ID: options.companyId || null,
          Simulated_Role: options.simulatedRole || null,
          Brand_ID: options.brandId || null,
          Reason: options.reason || null,
        }),
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || 'Impersonation failed');
      }

      const sessionData = result.data.Records?.[0];

      if (!sessionData) {
        throw new Error('Invalid response from impersonation API');
      }

      // Build impersonation session state
      const session: ImpersonationSession = {
        active: sessionData.impersonation_active,
        session_id: sessionData.session_id,
        client: sessionData.client,
        company: sessionData.company,
        role: sessionData.role,
        brand: sessionData.brand,
        started_at: sessionData.started_at,
        reason: sessionData.reason,
      };

      // Update state
      setImpersonation(session);
      setSelectedClientId(options.clientId);
      setSelectedCompanyId(options.companyId || null);
      setHasClientContext(true);

      if (sessionData.role?.effective_role) {
        setSimulatedRoleState(sessionData.role.effective_role as SimulatedRole);
      }

      if (sessionData.brand) {
        setActiveBrand(sessionData.brand);
      }

      // Persist to localStorage
      localStorage.setItem('selectedClientId', options.clientId);
      if (options.companyId) {
        localStorage.setItem('selectedCompanyId', options.companyId);
      }
      localStorage.setItem('impersonation', JSON.stringify(session));

      console.log('[AuthContext] Impersonation started:', session);

      return session;
    } catch (error) {
      console.error('[AuthContext] Impersonation failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * End impersonation and return to Super Admin mode
   */
  const endImpersonation = async (): Promise<void> => {
    console.log('[AuthContext] endImpersonation called');

    if (!user?.super_admin) {
      return;
    }

    try {
      if (accessToken) {
        // Call API to log the end of impersonation
        await fetch(`${API_GATEWAYS.CORE}${API_ENDPOINTS.SUPERADMIN.IMPERSONATE}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            Client_ID: null, // Null Client_ID signals end of impersonation
          }),
        });
      }
    } catch (error) {
      console.error('[AuthContext] Error ending impersonation:', error);
      // Continue with local cleanup even if API fails
    }

    // Clear all impersonation state
    setImpersonation(defaultImpersonation);
    setSelectedClientId(null);
    setSelectedCompanyId(null);
    setHasClientContext(false);
    setSimulatedRoleState(null);
    setActiveBrand(null);

    localStorage.removeItem('selectedClientId');
    localStorage.removeItem('selectedCompanyId');
    localStorage.removeItem('impersonation');

    console.log('[AuthContext] Impersonation ended');
  };

  /**
   * Change simulated role during active impersonation
   */
  const setSimulatedRole = async (role: SimulatedRole): Promise<void> => {
    console.log('[AuthContext] setSimulatedRole called:', role);

    if (!user?.super_admin || !impersonation.active || !impersonation.client) {
      throw new Error('No active impersonation session');
    }

    // Re-impersonate with new role
    await impersonate({
      clientId: impersonation.client.Client_ID,
      companyId: impersonation.company?.Company_ID,
      simulatedRole: role,
      brandId: activeBrand?.id,
      reason: `Role changed to ${role}`,
    });
  };

  /**
   * Change brand during active impersonation
   */
  const setBrand = async (brandId: number | null): Promise<void> => {
    console.log('[AuthContext] setBrand called:', brandId);

    if (!user?.super_admin || !impersonation.active || !impersonation.client) {
      throw new Error('No active impersonation session');
    }

    // Re-impersonate with new brand
    await impersonate({
      clientId: impersonation.client.Client_ID,
      companyId: impersonation.company?.Company_ID,
      simulatedRole: simulatedRole || undefined,
      brandId: brandId || undefined,
      reason: brandId ? `Brand changed to ${brandId}` : 'Brand reset to default',
    });
  };

  const forgotPassword = async (email: string) => {
    try {
      const response = await fetch(`${API_GATEWAYS.CORE}/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || 'Password reset failed');
      }

      // Success - verification code sent to email
    } catch (error) {
      console.error('Forgot password error:', error);
      throw error;
    }
  };

  const confirmForgotPassword = async (email: string, code: string, newPassword: string) => {
    try {
      const response = await fetch(`${API_GATEWAYS.CORE}/auth/confirm-forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, code, newPassword }),
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || 'Password reset confirmation failed');
      }

      // Success - password has been reset
    } catch (error) {
      console.error('Confirm forgot password error:', error);
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    accessToken,
    isAuthenticated: !!user && !!accessToken,
    isLoading,
    errorMessage,
    selectedClientId,
    selectedCompanyId,
    hasClientContext,
    // Impersonation state
    impersonation,
    simulatedRole,
    activeBrand,
    // Auth methods
    login,
    logout,
    refreshToken,
    forgotPassword,
    confirmForgotPassword,
    // Registration methods
    register,
    confirmRegistration,
    resendVerificationCode,
    clearError,
    // Impersonation methods
    setClientContext,
    clearClientContext,
    impersonate,
    endImpersonation,
    setSimulatedRole,
    setBrand,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
